import type {
  AllergyIntolerance,
  AllergyIntoleranceReaction,
  CodeableConcept,
  Patient,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import {
  ALLERGY_UNKNOWN_SYSTEM,
  ELATION_ALLERGY_MEDISPANDNID_SYSTEM,
  ELATION_ALLERGY_MEDISPANID_SYSTEM,
  EXTERNAL_RESOURCE_JSON_SYSTEM,
  HL7_ALLERGY_INTOLERANCE_LINK,
} from '#src/codeSystems.ts'
import { elationSandboxIdentifier, extractElationId } from '#src/elation.ts'
import { invariant } from '#src/invariant.ts'
import { createReference } from '#src/references.ts'
import { build } from '#src/utils.ts'

export const allergySchema = z.object({
  id: z.number(),
  status: z.enum(['Active', 'Inactive']),
  start_date: z
    .string()
    .optional()
    .nullable()
    .refine(
      (date) => !date || !Number.isNaN(Date.parse(date)),
      'Invalid date format'
    ),
  reaction: z.string(),
  name: z.string(),
  severity: z.string().optional().nullable(), // assuming severity can be an empty string, optional, and nullable
  medispanid: z.string().optional().nullable(),
  medispandnid: z.string().optional().nullable(),
  patient: z.number(),
  created_date: z
    .string()
    .optional()
    .nullable()
    .refine(
      (date) => !date || !Number.isNaN(Date.parse(date)),
      'Invalid date format'
    ),
  deleted_date: z
    .string()
    .optional()
    .nullable()
    .refine(
      (date) => !date || !Number.isNaN(Date.parse(date)),
      'Invalid date format'
    ),
})

export type Allergy = z.infer<typeof allergySchema>

const clinicalStatusMap: Record<Allergy['status'], CodeableConcept> = {
  Active: {
    coding: [
      {
        system: HL7_ALLERGY_INTOLERANCE_LINK,
        code: 'active',
        display: 'Active',
      },
    ],
  },
  Inactive: {
    coding: [
      {
        system: HL7_ALLERGY_INTOLERANCE_LINK,
        code: 'inactive',
        display: 'Inactive',
      },
    ],
  },
}

interface SeverityByText {
  [severity: string]: AllergyIntoleranceReaction['severity']
}

// The elation allergy API `severity` field does not claim to be an enum but the UI appears to restrict the input to a select with these options
// that align with the FHIR spec https://arc.net/l/quote/xwyxjvor
const severityMap: SeverityByText = {
  mild: 'mild',
  moderate: 'moderate',
  severe: 'severe',
} as const

export function fromFhir(
  allergy: AllergyIntolerance,
  patient: Patient
): Omit<Allergy, 'id'> & { id?: number } {
  const id = extractElationId(allergy)
  const patientId = extractElationId(patient)
  invariant(patientId, 'Patient ID not found')

  return {
    id,
    patient: patientId,
    status:
      codeFromCodeableConcept(allergy.clinicalStatus) === 'active'
        ? 'Active'
        : 'Inactive',
    start_date: allergy.onsetDateTime,
    reaction: allergy.reaction?.[0]?.manifestation?.[0]?.text ?? '',
    name: allergy.code?.text ?? allergy.code?.coding?.[0]?.display ?? '',
    severity: allergy.reaction?.[0]?.severity,
    medispanid: allergy.code?.coding?.find(
      (c) => c.system === ELATION_ALLERGY_MEDISPANID_SYSTEM
    )?.code,
  }
}

export function toFhir(allergy: Allergy, patient: Patient): AllergyIntolerance {
  const clinicalStatus = clinicalStatusMap[allergy.status]
  const severity = severityMap[allergy.severity?.toLowerCase()?.trim() ?? '']

  const coding: CodeableConcept['coding'] = compact([
    allergy.medispanid && {
      system: ELATION_ALLERGY_MEDISPANID_SYSTEM,
      code: allergy.medispanid,
      display: allergy.name,
    },
    allergy.medispandnid && {
      system: ELATION_ALLERGY_MEDISPANDNID_SYSTEM,
      code: allergy.medispandnid,
      display: allergy.name,
    },
  ])
  if (coding.length === 0) {
    // fallback to capture some allergy data despite not knowing the system
    coding.push({
      system: ALLERGY_UNKNOWN_SYSTEM,
      display: allergy.name,
    })
  }

  return build('AllergyIntolerance', {
    identifier: [elationSandboxIdentifier(allergy.id.toString())],
    patient: createReference(patient),
    clinicalStatus,
    code: {
      coding,
    },
    type: 'allergy',
    onsetDateTime: allergy.start_date ?? undefined,
    reaction: allergy.reaction?.trim()
      ? [
          {
            manifestation: [
              {
                text: allergy.reaction,
              },
            ],
            severity,
          },
        ]
      : undefined,
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml">${allergy.name}</div>`,
    },
    extension: [
      {
        url: EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(allergy),
      },
    ],
  })
}
